/**
 * What a lead asked for, in the space of a chip.
 *
 * The sync writes the plan column as "Books +5" — forty characters cannot
 * hold six product names — so every screen showing it was saying how many
 * without ever saying which. "+5" is a number standing in for five things
 * somebody wanted the names of.
 *
 * The chip still leads with the first, because a row has no room for six,
 * and gives the rest on hover or a click. Both, because hover is quicker
 * where there is a pointer and does not exist where there is not.
 *
 * ## Why the list is a portal
 *
 * It opens inside a table cell, a modal panel and a card, and all three clip
 * their children — a sticky column needs a background, a panel has rounded
 * corners to cut against, a scroller has to scroll. Positioned inside any of
 * them the list came out sliced off. Rendered at the end of the document and
 * placed against the chip's own rectangle, there is nothing left to clip it.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../lib/icons.jsx';

/* Room for the list plus a margin; below the chip if it fits, above if not. */
const GAP = 6;
const EDGE = 12;

export default function PlanChip({ list, label, className = '' }) {
  const items = (list || []).filter(Boolean);
  /* Whatever the row already shows, or the shorthand worked out from the
     list when nobody passed one. */
  const face = label || (items.length
    ? `${items[0]}${items.length > 1 ? ` +${items.length - 1}` : ''}`
    : '');

  const [open, setOpen] = useState(false);
  const [at, setAt] = useState(null);
  const chip = useRef(null);
  const pop = useRef(null);

  /*
   * Placed by measuring, not by arithmetic.
   *
   * The interface draws at `zoom: .7`, so a length written into `top` and a
   * length read back from getBoundingClientRect() are not in the same units,
   * and any sum of the two is wrong by a factor nobody should have to
   * remember. So the list is drawn at fixed 0,0 first, measured there, and
   * moved by the difference between where it landed and where it should be.
   * Both sides of that subtraction come from the same call, so whatever the
   * units are, they cancel.
   *
   * Done in a layout effect: measured after the list exists and before the
   * browser paints it, so it never appears in the wrong place and jumps.
   */
  useLayoutEffect(() => {
    if (!open || !chip.current || !pop.current) return;
    const c = chip.current.getBoundingClientRect();
    const p = pop.current.getBoundingClientRect();

    /* Below the chip if it fits, above it if not - and never off the side. */
    const wantTop = c.bottom + p.height + GAP <= window.innerHeight - EDGE
      ? c.bottom + GAP
      : Math.max(EDGE, c.top - GAP - p.height);
    const wantLeft = Math.min(Math.max(EDGE, c.left), window.innerWidth - p.width - EDGE);

    setAt({ top: wantTop - p.top, left: wantLeft - p.left });
  }, [open, items.length]);

  /* A page that moves under an open list leaves it stranded beside nothing. */
  useEffect(() => {
    if (!open) return undefined;
    const shut = () => setOpen(false);
    window.addEventListener('scroll', shut, true);
    window.addEventListener('resize', shut);
    return () => {
      window.removeEventListener('scroll', shut, true);
      window.removeEventListener('resize', shut);
    };
  }, [open]);

  if (!face) return null;

  /* Nothing more to tell: a chip that opens on the one item it is already
     showing is a control that does nothing. */
  if (items.length < 2) {
    return <span className={`dealchip plan ${className}`.trimEnd()}><Icon name="tag" />{face}</span>;
  }

  return (
    <span
      className={`planchip${open ? ' open' : ''} ${className}`.trimEnd()}
      ref={chip}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        className="dealchip plan"
        aria-expanded={open}
        onClick={e => { e.stopPropagation(); setOpen(o => !o); }}
      >
        <Icon name="tag" />{face}
        <Icon name="chev" />
      </button>

      {open && createPortal(
        <span
          className="planpop"
          ref={pop}
          /* Drawn at 0,0 to be measured, and invisible while it is there. */
          style={at ? { top: at.top, left: at.left } : { top: 0, left: 0, opacity: 0 }}
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
          onClick={e => e.stopPropagation()}
        >
          <b>Asked about {items.length} products</b>
          <span className="planlist">
            {items.map(p => <span key={p}><Icon name="check" />{p}</span>)}
          </span>
        </span>,
        document.body
      )}
    </span>
  );
}
